import { useState, useEffect, useRef, useCallback } from 'react';
import { getFileType } from '../utils/photoHelpers';
import api from '../../../api';

/**
 * Custom hook for managing video players and stream data
 * @param {Array} videoFiles - Array of video filenames to manage
 * @param {boolean} isActive - Whether the video manager is active
 * @returns {Object} Video management functions and data
 */
export const useVideoManager = (videoFiles = [], isActive = true) => {
  // eslint-disable-next-line no-unused-vars
  const [videoPlayers, setVideoPlayers] = useState(new Map());
  const [videoStreamData, setVideoStreamData] = useState(new Map());
  const fetchingVideosRef = useRef(new Set()); // Track videos currently being fetched

  // Fetch video stream URLs when videos are provided
  useEffect(() => {
    if (!isActive || !videoFiles.length) return;

    const fetchVideoStreams = async () => {
      // Detect mobile device once for all videos
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      // Get device memory if available (for advanced optimizations)
      const deviceMemory = navigator.deviceMemory || null;

      // Get network type if available
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const networkType = connection ? connection.effectiveType : null;

      for (const filename of videoFiles) {
        if (!videoStreamData.has(filename) && getFileType(filename) === 'video' && !fetchingVideosRef.current.has(filename)) {
          fetchingVideosRef.current.add(filename); // Mark as being fetched

          try {
            const token = localStorage.getItem('weddingToken');
            
            // Skip if no token - user needs to re-authenticate
            if (!token) {
              console.warn(`useVideoManager: No auth token available for ${filename}, skipping`);
              fetchingVideosRef.current.delete(filename);
              continue;
            }

            console.log(`useVideoManager: Fetching stream URL for ${filename}`, {
              isMobile,
              deviceMemory,
              networkType
            });

            const response = await api.post('/video-stream', {
              fileName: filename,
              quality: 'high',
              mobile: isMobile,
              deviceMemory,
              networkType,
            }, {
              headers: { Authorization: `Bearer ${token}` },
            });

            console.log(`useVideoManager: Got stream URL for ${filename}`, {
              url: response.data.url?.substring(0, 100) + '...',
              contentType: response.data.contentType,
              size: response.data.size
            });

            setVideoStreamData(prev => new Map(prev.set(filename, response.data)));
          } catch (error) {
            // Only 401 is treated as auth error (token invalid/expired)
            // 403/404/500 are server-side issues (file not found, permissions, etc.)
            if (error.response?.status === 401) {
              console.warn(`useVideoManager: Auth error for ${filename} - token expired or invalid`);
              // Auth issue will be handled by global interceptor in api.js
            } else if (error.response?.status === 404) {
              console.warn(`useVideoManager: Video not found: ${filename}`);
            } else if (error.response?.status === 403) {
              console.warn(`useVideoManager: Permission denied for ${filename}`);
            } else {
              console.warn(`useVideoManager: Failed to fetch video stream for ${filename}:`, error.message || error);
            }
          } finally {
            fetchingVideosRef.current.delete(filename); // Remove from fetching set
          }
        }
      }
    };

    fetchVideoStreams();

    // Cleanup function to clear fetching state when videos change
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      fetchingVideosRef.current.clear();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, videoFiles]); // Intentionally excluding videoStreamData to prevent infinite loop

  // Cleanup function for Plyr players
  const cleanupPlayers = useCallback(() => {
    setVideoPlayers(prevPlayers => {
      prevPlayers.forEach((player) => {
        if (player && typeof player.destroy === 'function') {
          player.destroy();
        }
      });
      return new Map();
    });
  }, []);

  // Get stream data for a specific filename
  const getStreamData = useCallback((filename) => {
    return videoStreamData.get(filename);
  }, [videoStreamData]);

  // Check if stream data is loading
  const isStreamLoading = useCallback((filename) => {
    return !videoStreamData.has(filename);
  }, [videoStreamData]);

  return {
    setVideoPlayers,
    getStreamData,
    isStreamLoading,
    cleanupPlayers
  };
};
